<script setup lang="ts">
import {onMounted, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import * as yup from 'yup';
import {useField, useForm} from "vee-validate";
import PhoneNumberInput from './components/PhoneNumberInput.vue';
import ProfilePhotoField from "./components/ProfilePhotoField.vue";
import {getEmailDomain} from "./utils";
import UsernameField from "./components/UsernameField.vue";
import AuthLayout from "./layouts/AuthLayout.vue";
import EmailField from "./components/EmailField.vue";
import api from "./api.ts";


const route = useRoute();
const router = useRouter();

// State for error snackbar
const errorMessageSnackbar = ref(false);
const errorMessage = ref('');
const loading = ref(false);
const checkedAuth = ref(false);

const inviteExpired = ref(false);
const registrationSuccess = ref(false);

interface IFormData {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  user_icon: File | null;
  partner_id: string;
}

const forbiddenUsernames = [
  'admin', 'root', 'null', 'undefined', 'test', 'username', 'user', 'guest',
  'support', 'superuser', 'api', 'system', 'info', 'contact'
];

const schema = yup.object({
  username: yup
      .string()
      .trim()
      .required()
      .min(5, 'Username must be at least 5 characters.')
      .max(30, 'Username must be 30 characters or less.')
      .test(
          'not-forbidden',
          'This username is not allowed.',
          (value) => !forbiddenUsernames.includes(value?.toLowerCase() ?? '')
      )
      .label('Username'),
  partner_id: yup.string().label('Partner'),
  first_name: yup.string().required().label('First Name'),
  last_name: yup.string().required().label('Last Name'),
  email: yup.string().email().trim().required().label('Email'),
  phone: yup.string().trim().required()
      .label('Phone')
      .matches(/^\+[1-9]{1,2}[0-9]{3}[0-9]{3}([0-9]{4})$/gm, "Phone number must be a valid phone number"),
  user_icon: yup.mixed<File>().label('Profile Picture'),
});

const {handleSubmit} = useForm({
  validationSchema: schema,
})

const client_id = ref<string[]>([]);
const user_type = ref<'contact' | 'user'>('contact');

const disableEmailField = ref<boolean>(false);

const usernameFieldRef = ref<InstanceType<typeof UsernameField>>()
const emailFieldRef = ref<InstanceType<typeof EmailField>>()

const username = useField<string>('username')
const partner_id = useField<string>('partner_id')
const first_name = useField<string>('first_name')
const last_name = useField<string>('last_name')
const email = useField<string>('email')
const phone = useField<string>('phone')
const user_icon = useField<File | null>('user_icon');

const onSubmit = handleSubmit(async (values: IFormData) => {
  if (
      (usernameFieldRef.value?.usernameAvailable !== true) || (emailFieldRef.value?.emailAvailable !== true)
  ) return;

  await submitRegistration(values)
})

const submitRegistration = async (values: IFormData) => {
  try {
    const token = route.query.token;

    if (!token || typeof token !== 'string') {
      throw new Error("Invite token is missing or invalid.");
    }

    const payload = {
      token,
      role: client_id.value ? [client_id.value] : [],
      type: user_type.value,
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.email,
      phone: values.phone,
      username: values.username,
      partner_id: values.partner_id,
    };

    const response = await api.post('/api/v4/invites/redeem', payload);
    const user_id: string = response.data.user_id;

    if (values.user_icon) {
      const photoData = new FormData();
      photoData.append('file', values.user_icon);

      await api.post(`/api/v4/users/${user_id}/photo`, photoData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
    }

    // Set flag instead of redirecting
    registrationSuccess.value = true;

  } catch (err: any) {
    console.error('Registration failed:', err);
    errorMessage.value =
        err.response?.data?.error ||
        err.message ||
        'Something went wrong during registration.';
    errorMessageSnackbar.value = true;
  }
};

const partners = ref([]);

const validateInvite = async () => {
  await api.get(`/api/v4/invites/${route.query.token}`).then(async (response) => {
    const isExpired = (new Date(response.data.invite.expires_at) < new Date()) || response.data.invite.used;
    if (isExpired) {
      inviteExpired.value = true;
      return;
    } else {
      email.value.value = response.data.invite.email;
      disableEmailField.value = true;
      client_id.value = response.data.invite.role;
      user_type.value = response.data.invite.user_type;
      const domain = getEmailDomain(response.data.invite.email);

      await api.get(`/api/v4/partners/${domain}`).then(async (response) => {
        console.log(response);
        partners.value.push(response.data.partner);
        partner_id.value.value = response.data.partner.id;
      }).catch((error) => {
        console.log(error);
      })
    }
  }).catch(async (error) => {
    console.log(error);
    await router.push('/login');
  });
}

onMounted(async () => {
  if (!route.query.token) {
    await router.push('/login');
  }
  await validateInvite();
})

</script>

<template>
  <AuthLayout>
    <!-- Expired Invite Message -->
    <v-sheet
        v-if="inviteExpired && !loading"
        width="450"
        class="mx-auto pa-12 text-center"
        rounded="lg"
        elevation="8"
    >
      <v-icon class="mb-5" color="error" icon="mdi-close-circle" size="112"/>
      <h2 class="text-h5 mb-6">Invite Expired</h2>
      <p class="mb-4 text-medium-emphasis text-body-2">
        This invitation is no longer valid. It may have already been used or expired.
      </p>
      <p class="text-body-2">Please request a new invite to register.</p>
      <v-btn color="primary" class="mt-6" @click="router.push('/login')">Back to Login</v-btn>
    </v-sheet>
    <!-- Registration Success Message -->
    <v-sheet
        v-else-if="registrationSuccess && !loading"
        width="450"
        class="mx-auto pa-12 text-center"
        rounded="lg"
        elevation="8"
    >
      <v-icon class="mb-5" color="success" icon="mdi-check-circle" size="112"/>
      <h2 class="text-h5 mb-6">Account Created Successfully</h2>
      <p class="mb-4 text-medium-emphasis text-body-2">
        Your account has been created! Please check your email for your temporary password.
      </p>
<!--      <v-btn color="primary" class="mt-6" @click="router.push('/login')">Log In</v-btn>-->
    </v-sheet>
    <v-sheet width="450" class="mx-auto my-4 pa-12 text-center" rounded="lg" elevation="8"
             v-else-if="!loading && !checkedAuth && !inviteExpired">
      <div class="text-center mb-6">
        <h1 class="text-h5 font-weight-bold mb-2">Welcome! 🎉</h1>
        <p class="text-body-1">
          You’ve been invited to join our platform. Please fill out the form below to create your account.
        </p>
        <p class="text-body-2 text-grey">
          Your username will be used to log in and identify you across applications, so make it something memorable—
          <strong>you won’t be able to change it later.</strong>
        </p>
      </div>

      <v-divider class="my-4"/>
      <h2 class="text-subtitle-1 font-weight-medium mb-3">Profile Photo</h2>

      <ProfilePhotoField name="user_icon" v-model="user_icon.value.value"/>

      <!-- Account Info -->
      <v-divider class="mb-4"/>
      <h2 class="text-subtitle-1 font-weight-medium mb-3">Account Info</h2>

      <UsernameField ref="usernameFieldRef" name="username" v-model="username.value.value"/>

<!--      <v-select class="my-2" label="Partner" disabled-->
<!--                v-model="partner_id.value.value"-->
<!--                :error-messages="partner_id.errorMessage.value"-->
<!--                :items="partners"-->
<!--                item-value="id"-->
<!--                item-title="title"-->
<!--      ></v-select>-->

      <!-- Personal Details -->
      <v-divider class="my-4"/>
      <h2 class="text-subtitle-1 font-weight-medium mb-3">Personal Details</h2>

      <v-text-field class="my-2" label="First Name*" placeholder="John" v-model="first_name.value.value"
                    :error-messages="first_name.errorMessage.value"></v-text-field>
      <v-text-field class="my-2" label="Last Name*" placeholder="Doe" v-model="last_name.value.value"
                    :error-messages="last_name.errorMessage.value"></v-text-field>

      <!-- Contact Info -->
      <v-divider class="my-4"/>
      <h2 class="text-subtitle-1 font-weight-medium mb-3">Contact Info</h2>

      <EmailField ref="emailFieldRef" name="email" v-model="email.value.value" :disabled="disableEmailField"/>
      <PhoneNumberInput
          v-model="phone.value.value"
          label="Mobile Phone*"
          hint="Formats like (801) 555-1234 are accepted"
          country="US"
      />

      <!-- Submit Button -->
      <v-divider class="my-4"/>
      <span class="text-sm-caption">* indicates required fields.</span>
      <v-btn color="primary" block class="mt-2" size="large" @click="onSubmit">
        <span class="mx-2">Submit</span>
      </v-btn>
    </v-sheet>
    <v-snackbar v-model="errorMessageSnackbar">
      {{ errorMessage }}

      <template v-slot:actions>
        <v-btn color="pink" variant="text" @click="errorMessageSnackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
    <!--    <ThemeToggleFab></ThemeToggleFab>-->
  </AuthLayout>
  <!--  </div>-->
</template>

<style scoped>

</style>